import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeMerge } from '@fortawesome/pro-light-svg-icons/faCodeMerge';
import { TypedMemo } from '../../../common/types';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { CatalogItem, CatalogMergeCandidate, CatalogMergeOption } from '../../../../redux/api/fymAPI.types';
import { mergeMessages } from './mergeMessages';
import { useMergeSongs } from './useMergeSongs';
import { MergeOptionRow } from './MergeOptionRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the merge dialogs; each part uses its own slice of it */
import styles from './Merge.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ClaimMergePromptProps {
  accountId: number;
  /** The row that the claim made. */
  item: CatalogItem;
  /** The rows that can be the same song (the claim answer `mergeCandidates`). */
  candidates: CatalogMergeCandidate[];
  /** Gets the row the artist goes on with: the merged row, or the claimed row on "Keep separate". */
  onDone: (item: CatalogItem) => void;
}

/**
 * The claim result when the claim looks like a song that is there already (design catalog-merge
 * a4): "This looks like <song>. Merge?" / "Keep separate". The candidate stays and the new row
 * goes. With two candidates or more, the artist picks one in the list first.
 */
const ClaimMergePromptComponent: React.FC<ClaimMergePromptProps> = ({ accountId, item, candidates, onDone }) => {
  const intl = useIntl();
  const [selectedUuid, setSelectedUuid] = useState(candidates[0]?.uuid);
  const { merge, isLoading, error, errorKind } = useMergeSongs({ accountId });
  const options = useMemo<CatalogMergeOption[]>(
    () => candidates.map((row) => ({ ...row, likelyDuplicate: true, keepUuid: row.uuid })),
    [candidates]
  );
  const selected = options.find((row) => row.uuid === selectedUuid);
  const isMany = options.length > 1;

  const keepSeparate = useCallback(() => onDone(item), [item, onDone]);
  const mergeIt = useCallback(() => {
    if (selected === undefined) {
      return;
    }
    merge(selected.uuid, item.title, item.uuid)
      .then((kept) => {
        if (kept !== null) {
          onDone(kept);
        }
      })
      .catch(console.error);
  }, [item.title, item.uuid, merge, onDone, selected]);

  const mergeLabel =
    !isMany && selected !== undefined
      ? intl.formatMessage(mergeMessages.claimMergeWith, { title: selected.title })
      : intl.formatMessage(mergeMessages.claimMergeWithMany);

  return (
    <>
      <ModalBody>
        <div className={styles.stack}>
          <span className={styles.optionName}>
            {intl.formatMessage(mergeMessages.claimedTitle, { title: item.title })}
          </span>
          <div className={styles.prompt} data-testid="claim-merge-prompt">
            <span className={styles.optionName}>
              {isMany || selected === undefined
                ? intl.formatMessage(mergeMessages.claimMany)
                : intl.formatMessage(mergeMessages.claimOne, { title: selected.title })}
            </span>
            {selected !== undefined && (
              <span className={styles.optionSub}>
                {intl.formatMessage(mergeMessages.claimLead, { title: selected.title })}
              </span>
            )}
            {isMany && (
              <div className={styles.list} role="radiogroup">
                {options.map((option) => (
                  <MergeOptionRow
                    key={option.uuid}
                    option={option}
                    isSelected={option.uuid === selectedUuid}
                    onSelect={setSelectedUuid}
                  />
                ))}
              </div>
            )}
          </div>
          {error !== undefined && errorKind === 'changed' && (
            <p className={styles.empty}>{intl.formatMessage(mergeMessages.changed)}</p>
          )}
          {error !== undefined && errorKind === 'failed' && <ErrorAlert error={error} />}
        </div>
      </ModalBody>
      <ModalFooter>
        <SMLButton onClick={keepSeparate} disabled={isLoading}>
          {intl.formatMessage(mergeMessages.claimKeepSeparate)}
        </SMLButton>
        <SMLButton
          variant="primary"
          icon={<FontAwesomeIcon icon={faCodeMerge} />}
          onClick={mergeIt}
          disabled={isLoading || selected === undefined || errorKind === 'changed'}
        >
          {mergeLabel}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

export const ClaimMergePrompt = TypedMemo(ClaimMergePromptComponent);
